import { createSlice } from '@reduxjs/toolkit';
import type { CountryCode } from '../../services/api/countryCodeService';
import {
  fetchCountryCodes, fetchActiveCountryCodes,
  createCountryCode, updateCountryCode, deleteCountryCode,
  activateCountryCode, deactivateCountryCode,
} from '../thunks/countryCodeThunks';

export interface CountryCodeFiltersState {
  continentCode: string; // '' = all
  search: string;        // enriched free-text (matches capital too)
}

export interface CountryCodeState {
  countryCodes: CountryCode[];
  activeCountryCodes: CountryCode[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
  filters: CountryCodeFiltersState;
}

const initialState: CountryCodeState = {
  countryCodes: [],
  activeCountryCodes: [],
  totalCount: 0,
  pageNumber: 1,
  pageSize: 20,
  isLoading: false,
  error: null,
  filters: {
    continentCode: '',
    search: '',
  },
};

const countryCodeSlice = createSlice({
  name: 'countryCode',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setContinentFilter: (state, action: { payload: string }) => {
      state.filters.continentCode = action.payload;
    },
    setSearchFilter: (state, action: { payload: string }) => {
      state.filters.search = action.payload;
    },
    clearFilters: (state) => {
      state.filters.continentCode = '';
      state.filters.search = '';
    },
  },
  extraReducers: (builder) => {
    // --- Fetch Country Codes (admin, paginated) ---
    builder
      .addCase(fetchCountryCodes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCountryCodes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.countryCodes = action.payload.countryCodes;
        state.totalCount = action.payload.totalCount;
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchCountryCodes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // --- Fetch Active Country Codes (dropdown) ---
    builder
      .addCase(fetchActiveCountryCodes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActiveCountryCodes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activeCountryCodes = action.payload;
      })
      .addCase(fetchActiveCountryCodes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // --- Create Country Code ---
    // Note: rejected errors are handled locally via .unwrap() in the component (formError)
    builder
      .addCase(createCountryCode.pending, (state) => { state.isLoading = true; })
      .addCase(createCountryCode.fulfilled, (state) => { state.isLoading = false; })
      .addCase(createCountryCode.rejected, (state) => { state.isLoading = false; });

    // --- Update Country Code ---
    // Note: rejected errors are handled locally via .unwrap() in the component (formError)
    builder
      .addCase(updateCountryCode.pending, (state) => { state.isLoading = true; })
      .addCase(updateCountryCode.fulfilled, (state) => { state.isLoading = false; })
      .addCase(updateCountryCode.rejected, (state) => { state.isLoading = false; });

    // --- Delete Country Code ---
    builder
      .addCase(deleteCountryCode.fulfilled, (state, action) => {
        state.countryCodes = state.countryCodes.filter(c => c.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      });

    // --- Activate / Deactivate (optimistic) ---
    builder
      .addCase(activateCountryCode.pending, (state, action) => {
        const code = state.countryCodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = true; code.status = 'active'; }
      })
      .addCase(activateCountryCode.fulfilled, (state, action) => {
        const code = state.countryCodes.find(c => c.id === action.payload);
        if (code) { code.isActive = true; code.status = 'active'; }
      })
      .addCase(activateCountryCode.rejected, (state, action) => {
        const code = state.countryCodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = false; code.status = 'inactive'; }
      })
      .addCase(deactivateCountryCode.pending, (state, action) => {
        const code = state.countryCodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = false; code.status = 'inactive'; }
      })
      .addCase(deactivateCountryCode.fulfilled, (state, action) => {
        const code = state.countryCodes.find(c => c.id === action.payload);
        if (code) { code.isActive = false; code.status = 'inactive'; }
      })
      .addCase(deactivateCountryCode.rejected, (state, action) => {
        const code = state.countryCodes.find(c => c.id === action.meta.arg.id);
        if (code) { code.isActive = true; code.status = 'active'; }
      });
  },
});

export const { clearError, setContinentFilter, setSearchFilter, clearFilters } = countryCodeSlice.actions;
export default countryCodeSlice.reducer;
